import { solutionPath,VALUE_PROPS } from "@/3-features/solutions/valueProps";
import type { Audience, NavEntry } from "@/5-shared/types";

import { PATHS, PRODUCER_ROLES } from "./routes";

export type { NavEntry };

/** Shallow translator type, the full i18next `TFunction` here instantiates deep
 *  enough to push the cumulative TS2589 budget over; only string keys are used.
 *  Loose signature so callers can pass the real `TFunction`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Tr = (...args: any[]) => string;

export function getNavs(t: Tr): Record<Audience, NavEntry[]> {
  const decExplorer = { label: t("nav.decExplorer"), to: PATHS.explorer, desc: t("nav.decExplorerDesc") };
  const sharedResources = [
    { label: t("nav.faq"), to: PATHS.faq, desc: t("nav.faqDesc") },
    { label: t("nav.blog"), to: PATHS.blog, desc: t("nav.blogDesc") },
  ];
  const resourcesFor = (items: Extract<NavEntry, { kind: "group" }>["items"]): NavEntry => ({
    kind: "group",
    label: t("nav.resources"),
    base: "/resources",
    items,
  });
  // dec-explorer is company-only; producers get faq + blog.
  const companiesResources = resourcesFor([decExplorer, ...sharedResources]);
  const producersResources = resourcesFor(sharedResources);

  return {
    companies: [
      { kind: "link", label: t("nav.home"), to: PATHS.companies },
      {
        kind: "group",
        label: t("nav.solutions"),
        base: PATHS.companiesSolutions,
        items: VALUE_PROPS.map((vp) => {
          const label = t(`nav.solutionLabels.${vp.slug}`);
          // desc = the bento chip, same key so the two stay identical
          return { label, desc: t(`nav.solutionCategories.${vp.slug}`), to: solutionPath(vp) };
        }),
      },
      { kind: "link", label: t("nav.howItWorks"), to: PATHS.companiesProduct },
      { kind: "link", label: t("nav.aboutUs"), to: PATHS.about },
      companiesResources,
    ],
    producers: [
      { kind: "link", label: t("nav.home"), to: PATHS.producers },
      {
        kind: "group",
        label: t("nav.whoWeServe"),
        base: "/producer/serve",
        items: PRODUCER_ROLES.map((r) => {
          const label = t(`nav.roleLabels.${r.slug}`);
          const desc = t(`nav.roleDescs.${r.slug}`);
          return { label, to: r.path, desc };
        }),
      },
      { kind: "link", label: t("nav.howItWorks"), to: PATHS.producersProduct },
      { kind: "link", label: t("nav.aboutUs"), to: PATHS.about },
      producersResources,
    ],
  };
}

/**
 * Stripped navbar for a cold arrival on a standalone resources page (a visitor
 * with no stored audience, see the `data-known` probe in +Head). Home routes to
 * the landing page (which the fresh landing URL needs for index authority),
 * followed by the Resources dropdown minus dec-explorer with About us folded
 * in. No company/producer funnel links.
 */
export function getColdResourcesNav(t: Tr): NavEntry[] {
  return [
    { kind: "link", label: t("nav.home"), to: PATHS.home },
    {
      kind: "group",
      label: t("nav.resources"),
      base: "/resources",
      items: [
        { label: t("nav.faq"), to: PATHS.faq, desc: t("nav.faqDesc") },
        { label: t("nav.blog"), to: PATHS.blog, desc: t("nav.blogDesc") },
        { label: t("nav.aboutUs"), to: PATHS.about },
      ],
    },
  ];
}

export function getSwitch(t: Tr): Record<Audience, { label: string; to: string }> {
  return {
    companies: { label: t("nav.switchToProducers"), to: PATHS.producers },
    producers: { label: t("nav.switchToCompanies"), to: PATHS.companies },
  };
}
